import { Box, Stack, Typography } from '@mui/material';
import { useLanguageSelector } from '@retail/app/hooks';
import {
  CampaignDetailsFilters,
  CampaignItemsActionToolbar,
  CampaignItemsBaseColumns,
  CampaignItemsTable,
  DeleteItemsDialog,
  DistributeCampaignDialog,
  useGetCampaignItemsBaseColumns,
} from '@retail/campaign/components';
import { BaseCampaignItem, CampaignBase } from '@retail/campaign/types';
import { Breadcrumbs, Paper, TagHeading } from '@shared/components';
import { useSearchParams } from 'react-router';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { MgItemContainer, MgItemDialogContainer } from '@retail/products/containers';
import { useDisclosure, usePagination } from '@shared/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { useCampaignFiltering, useDeleteCampaignItemsActions } from '@retail/campaign/context';
import { mgPrisRoutes } from '@retail/mgpris/config';
import {
  DeleteDialog,
  DiagonalMosaicImgBg,
  ErrorFallback,
  FilterChipContainer,
  SentryErrorBoundary,
  ToggleFiltersToolbar,
  ToggleFiltersToolbarButton,
} from '@retail/components';
import { useToast } from '@retail/hooks';
import { GridColDef } from '@mui/x-data-grid';
import { CampaignTenantType } from '@retail/campaign/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';

interface CampaignDetailsActions {
  isLoadingDistribution: boolean;
  handleCampaignDistribution: () => Promise<void>;
}

type DialogFunctions = { onClose: () => void; open: boolean };

interface CampaignDetailsContainerBaseProps<T extends CampaignBase<U>, U extends BaseCampaignItem> {
  campaign: T;
  campaignDetailsActions: CampaignDetailsActions;
  renderMetadata: () => ReactNode;
  renderDistributeDialogContent: () => ReactNode;
  renderAddItemsDialog: (props: DialogFunctions) => ReactNode;
  renderCTA?: (props: {
    onOpenAddItemsDialog: () => void;
    onOpenDistributeCampaignDialog: () => void;
  }) => ReactNode;
  renderEditCampaignItemsDialog: (props: { onClose: () => void; itemsToEdit: U[] }) => ReactNode;
  campaignItems: U[] | undefined;
  fetchItemsStatus: 'loading' | 'success' | 'error';
  refetchItems: () => void;
  composeItemsColumns: (baseColumns: CampaignItemsBaseColumns<U>) => GridColDef<U>[];
  includeVat: boolean;
  toggleIncludeVat: () => void;
  type: CampaignTenantType;
  handleSubmitSalesPrice: (item: U, salesPrice: number) => Promise<void>;
  handleSubmitPurchasePrice: (item: U, salesPrice: number) => Promise<void>;
  handleSubmitPurchasePeriod: (item: U, from?: string, to?: string) => Promise<void>;
  handleSubmitCampaignUnit: (item: U, campaignUnit: string) => Promise<void>;
}

export function CampaignDetailsContainerBase<
  T extends CampaignBase<U>,
  U extends BaseCampaignItem
>({
  campaign,
  campaignDetailsActions,
  renderMetadata,
  renderDistributeDialogContent,
  renderAddItemsDialog,
  renderCTA = () => null,
  renderEditCampaignItemsDialog,
  campaignItems,
  fetchItemsStatus,
  refetchItems,
  composeItemsColumns,
  includeVat,
  toggleIncludeVat,
  type,
  handleSubmitPurchasePrice,
  handleSubmitSalesPrice,
  handleSubmitCampaignUnit,
  handleSubmitPurchasePeriod,
}: CampaignDetailsContainerBaseProps<T, U>) {
  const t = useAppTFunction();
  const { language } = useLanguageSelector();
  const addItemsDialog = useDisclosure();
  const distributeCampaignDialog = useDisclosure();
  const deleteItemsDialog = useDisclosure();
  const editItemsDialog = useDisclosure();
  const [, setURLSearchParams] = useSearchParams();
  const toast = useToast();
  const [checkedItems, setCheckedItems] = useState<U[]>([]);
  const { appTenant } = useSelectedOrgUnit();

  const checkedItemsIds = useMemo(() => checkedItems.map((item) => item.id), [checkedItems]);
  const onCheckItems = useCallback(
    (itemIds: string[]) => {
      const checkedCampaignItems: U[] = itemIds.reduce((accItems, itemId) => {
        const campaignItem = campaignItems?.find((item) => item.id === itemId);
        return campaignItem ? [...accItems, campaignItem] : accItems;
      }, [] as U[]);

      setCheckedItems(checkedCampaignItems);
    },
    [campaignItems, setCheckedItems]
  );

  useEffect(() => {
    setURLSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addItemsDialog.isOpen]);

  const pagination = usePagination();

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(false);

  const {
    filteredCampaignItems,
    suppliers,
    productGroups,
    filters,
    setSearchQuery,
    setSupplierNumber,
    setProductGroupNumber,
    resetFilters,
  } = useCampaignFiltering({ campaignItems });

  const { isLoadingDelete, handleDeleteItem, handleDeleteItems } = useDeleteCampaignItemsActions({
    campaignId: campaign.id,
    type,
  });
  const [itemToDelete, setItemToDelete] = useState<U | undefined>(undefined);
  const onDeleteCheckedItems = useCallback(() => {
    handleDeleteItems(checkedItems.map((item) => item.id)).then(() => {
      setCheckedItems([]);
      deleteItemsDialog.onClose();
    });
  }, [checkedItems, deleteItemsDialog, handleDeleteItems]);

  const onEditCheckedItems = useCallback(() => {
    editItemsDialog.onOpen();
  }, [editItemsDialog]);

  const [selectedItem, setSelectedItem] = useState<BaseCampaignItem | null>(null);

  const onHandleCampaignDistribution = useCallback(() => {
    campaignDetailsActions
      .handleCampaignDistribution()
      .then(() => {
        distributeCampaignDialog.onClose();
      })
      .catch((error) => {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      });
  }, [distributeCampaignDialog, campaignDetailsActions, t, toast]);

  const handleCloseAddItemsDialog = useCallback(() => {
    resetFilters();
    addItemsDialog.onClose();
  }, [addItemsDialog, resetFilters]);

  const baseColumns = useGetCampaignItemsBaseColumns<U>({
    getEditableFields: campaign.editableItemFields,
    deleteItem: campaign.isCampaignEditable() ? setItemToDelete : undefined,
    setSelectedItem,
    handleSubmitSalesPrice,
    handleSubmitPurchasePrice,
    handleSubmitCampaignUnit,
    handleSubmitPurchasePeriod,
    includeVat,
    externalSourceType: appTenant.externalItemSource,
  });

  const campaignItemsTableColumns = useMemo(
    () => composeItemsColumns(baseColumns),
    [baseColumns, composeItemsColumns]
  );

  return (
    <Paper
      paddingY="dense"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        px: 0,
        pt: 2,
        position: 'relative',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      <DiagonalMosaicImgBg />
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} mx={3}>
        <TagHeading color="primary">
          <Breadcrumbs
            fontSize="md"
            items={[
              {
                label: t('campaign.campaignPlural'),
                href: mgPrisRoutes.campaigns.getFullLinkPath(),
              },
            ]}
          />
          {renderCTA({
            onOpenAddItemsDialog: addItemsDialog.onOpen,
            onOpenDistributeCampaignDialog: distributeCampaignDialog.onOpen,
          })}
        </TagHeading>
      </Stack>

      <Stack height="100%" bgcolor="inherit">
        <Box display="flex" px={3}>
          {renderMetadata()}
        </Box>

        <ToggleFiltersToolbar sx={{ justifyContent: 'space-between' }}>
          <FilterChipContainer
            allowedKeys={['searchQuery', 'supplierNumber', 'productGroupNumber'] as const}
            filterOptions={{ suppliers, productGroups }}
          />
          <Stack direction="row" alignItems="center" mr={2} spacing={2}>
            <Box>
              <Typography component="span" variant="body2">
                {`${t('myAssortment.showing')} `}
              </Typography>
              <Typography component="span" variant="body2" fontWeight={600}>
                {filteredCampaignItems?.length ?? 0}
              </Typography>
              <Typography component="span" variant="body2">
                {` ${t('myAssortment.of')} `}
              </Typography>
              <Typography component="span" variant="body2" fontWeight={600}>
                {campaignItems?.length ?? 0}
              </Typography>
              <Typography component="span" variant="body2">
                {` ${t('myAssortment.items')}`}
              </Typography>
            </Box>
            <ToggleFiltersToolbarButton showFilters={showFilters} toggleFilters={toggleFilters} />
          </Stack>
        </ToggleFiltersToolbar>

        <Stack flexDirection="row" flexGrow={1} overflow="auto">
          <Stack flexGrow={1}>
            <SentryErrorBoundary
              fallback={(props) => <ErrorFallback {...props} message={props.error.message} />}
            >
              <CampaignItemsTable
                language={language}
                items={filteredCampaignItems ?? []}
                status={fetchItemsStatus}
                refetchItems={refetchItems}
                columns={campaignItemsTableColumns}
                checkboxSelection={
                  campaign.isCampaignEditable()
                    ? { checkedItemIds: checkedItemsIds, checkItemsHandler: onCheckItems }
                    : undefined
                }
              />
            </SentryErrorBoundary>
          </Stack>

          <CampaignDetailsFilters
            searchTerm={filters.searchQuery ?? ''}
            disableFields={fetchItemsStatus !== 'success' || !campaignItems?.length}
            suppliers={suppliers}
            productGroups={productGroups}
            selectedSupplier={filters.supplierNumber ? Number(filters.supplierNumber) : null}
            selectedProductGroup={filters.productGroupNumber}
            showFilters={showFilters}
            includeVat={includeVat}
            toggleIncludeVat={toggleIncludeVat}
            selectSupplier={setSupplierNumber}
            selectProductGroup={setProductGroupNumber}
            setSearchTerm={setSearchQuery}
            setPage={pagination.setPage}
          />
        </Stack>

        {checkedItems.length > 0 && !!campaignItems && (
          <CampaignItemsActionToolbar
            totalItemsCount={campaignItems.length}
            checkedItemsCount={checkedItems.length}
            onUncheckItems={() => setCheckedItems([])}
            onEditItems={onEditCheckedItems}
            onRemoveItems={deleteItemsDialog.onOpen}
          />
        )}

        {selectedItem && (
          <MgItemDialogContainer
            open
            onClose={() => setSelectedItem(null)}
            itemNumber={selectedItem.mgItemNumber}
          >
            {({ mgItem }) => (
              <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
            )}
          </MgItemDialogContainer>
        )}

        {addItemsDialog.isOpen &&
          renderAddItemsDialog({ onClose: handleCloseAddItemsDialog, open: true })}

        {distributeCampaignDialog.isOpen && (
          <DistributeCampaignDialog
            campaignName={campaign.name}
            onClose={distributeCampaignDialog.onClose}
            onDistribute={onHandleCampaignDistribution}
            isLoading={campaignDetailsActions.isLoadingDistribution}
            content={renderDistributeDialogContent()}
          />
        )}

        {editItemsDialog.isOpen &&
          renderEditCampaignItemsDialog({
            onClose: () => {
              editItemsDialog.onClose();
              setCheckedItems([]);
            },
            itemsToEdit: checkedItems,
          })}

        {deleteItemsDialog.isOpen && (
          <DeleteItemsDialog
            onClose={deleteItemsDialog.onClose}
            onDelete={onDeleteCheckedItems}
            itemsCount={checkedItems.length}
            isLoading={isLoadingDelete}
          />
        )}
        {!!itemToDelete && (
          <DeleteDialog
            isOpen
            dialogTitle={t('campaign.columns.selection.remove.label')}
            message={t('campaign.actions.deleteItemConfirmation', {
              itemName: itemToDelete.primaryText,
            })}
            onClose={() => setItemToDelete(undefined)}
            onDelete={() => handleDeleteItem(itemToDelete.id)}
          />
        )}
      </Stack>
    </Paper>
  );
}
